import { z } from 'zod';
import { FormState } from 'react-hook-form';
import { mixTrack } from '@services/mixpanel';
import { SubPriceFormValues } from './exclusive.viewModel';

const PRICE_LIMITS = {
  monthly: {
    min: 1,
    max: 500,
    minMessage:
      'You may not charge less than $1 monthly for your subscription.',
    maxMessage:
      'You may not charge more than $500 monthly for your subscription',
    zeroMessage: 'Monthly subscription price cannot be $00.00.',
  },
  annual: {
    min: 10,
    max: 2000,
    minMessage:
      'You may not charge less than $10 annually for your subscription.',
    maxMessage:
      'You may not charge more than $2,000 annually for your subscription',
    zeroMessage: 'Annual subscription price cannot be $00.00.',
  },
};

const isValidPrice = (price?: string): boolean =>
  Boolean(price && price !== '0');
const isPriceTooLow = (price: string | undefined, min: number): boolean =>
  isValidPrice(price) && Number(price) < min;
const isPriceTooHigh = (price: string | undefined, max: number): boolean =>
  isValidPrice(price) && Number(price) > max;

const createValidator =
  (ctx: z.RefinementCtx) =>
  (condition: boolean, message: string, path: string) => {
    if (condition) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message,
        path: [path],
      });
    }
    return condition;
  };

const createValidators = (
  data: {
    subscriptionAnnualPrice?: string;
    subscriptionMonthlyPrice?: string;
    monthlySelected?: boolean;
    annualSelected?: boolean;
  },
  validate: (condition: boolean, message: string, path: string) => boolean,
) => {
  const hasMonthlyPrice = isValidPrice(data.subscriptionMonthlyPrice);
  const hasAnnualPrice = isValidPrice(data.subscriptionAnnualPrice);

  return {
    validateAtLeastOneSelected: () =>
      validate(
        !data.monthlySelected && !data.annualSelected,
        'At least one subscription price must be selected.',
        '',
      ),

    validateMonthlyPrice: () => {
      if (!data.monthlySelected) return false;

      if (
        validate(
          !hasMonthlyPrice,
          PRICE_LIMITS.monthly.zeroMessage,
          'subscriptionMonthlyPrice',
        )
      )
        return true;

      validate(
        isPriceTooLow(data.subscriptionMonthlyPrice, PRICE_LIMITS.monthly.min),
        PRICE_LIMITS.monthly.minMessage,
        'subscriptionMonthlyPrice',
      );

      validate(
        isPriceTooHigh(data.subscriptionMonthlyPrice, PRICE_LIMITS.monthly.max),
        PRICE_LIMITS.monthly.maxMessage,
        'subscriptionMonthlyPrice',
      );

      return false;
    },

    validateAnnualPrice: () => {
      if (!data.annualSelected) return false;

      if (
        validate(
          !hasAnnualPrice,
          PRICE_LIMITS.annual.zeroMessage,
          'subscriptionAnnualPrice',
        )
      )
        return true;

      validate(
        isPriceTooLow(data.subscriptionAnnualPrice, PRICE_LIMITS.annual.min),
        PRICE_LIMITS.annual.minMessage,
        'subscriptionAnnualPrice',
      );

      validate(
        isPriceTooHigh(data.subscriptionAnnualPrice, PRICE_LIMITS.annual.max),
        PRICE_LIMITS.annual.maxMessage,
        'subscriptionAnnualPrice',
      );

      return false;
    },

    validateAnnualHigherThanMonthly: () =>
      validate(
        hasMonthlyPrice &&
          hasAnnualPrice &&
          Number(data.subscriptionMonthlyPrice) >=
            Number(data.subscriptionAnnualPrice),
        'Annual subscription price must be higher than monthly',
        'subscriptionAnnualPrice',
      ),
  };
};

export const subPriceSchema = z
  .object({
    subscriptionAnnualPrice: z.string().optional(),
    subscriptionMonthlyPrice: z.string().optional(),
    monthlySelected: z.boolean().default(false),
    annualSelected: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    const validate = createValidator(ctx);
    const validators = createValidators(data, validate);

    validators.validateAtLeastOneSelected();
    validators.validateMonthlyPrice();
    validators.validateAnnualPrice();
    validators.validateAnnualHigherThanMonthly();
  });

export type SubscriptionPricePayload = {
  monthlyPriceCents: number | null;
  annualPriceCents: number | null;
};
export type VariablesFromPriceParams = {
  monthly?: string;
  annual?: string;
};

export const getVariablesFromPrice = (
  price: VariablesFromPriceParams,
): SubscriptionPricePayload => {
  const variables: SubscriptionPricePayload = {
    monthlyPriceCents: null,
    annualPriceCents: null,
  };
  if (price.monthly) {
    variables.monthlyPriceCents = convertDollarsToCents(Number(price.monthly));
  }
  if (price.annual) {
    variables.annualPriceCents = convertDollarsToCents(Number(price.annual));
  }
  return variables;
};

export const convertDollarsToCents = (price: number): number => {
  if (Number.isInteger(price)) {
    return price * 100;
  }
  const [int, dec] = price.toString().split('.');
  return parseInt(int) * 100 + parseInt(dec);
};

export const priceMixpanelEvents = (
  dirtyFields: FormState<SubPriceFormValues>['dirtyFields'],
  values: SubPriceFormValues,
) => {
  if (dirtyFields.subscriptionMonthlyPrice) {
    mixTrack({
      type: 'Exclusive Sub Monthly Price Set',
      payload: { Price: Number(values.subscriptionMonthlyPrice) },
    });
  }
  if (dirtyFields.subscriptionAnnualPrice) {
    mixTrack({
      type: 'Exclusive Sub Annual Price Set',
      payload: { Price: Number(values.subscriptionAnnualPrice) },
    });
  }
};
